import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import {
  Search,
  X,
  ArrowRight,
  TrendingUp,
  ChevronRight,
  ChevronDown,
  Sparkles,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { categoriesData } from "../../constant/categoriesData";
import { slugify } from "../../Common/slugify";
import {
  productImageUrl,
  productImageSrcSet,
  applyImageFallback,
} from "../../Common/productImage";
import NetworkOverlay from "./NetworkOverlay";
import "./HeroSection.css";

const SUGGESTION_LIMIT = 6;

// The catalogue is a static import, so flatten it once at module scope rather
// than on every render/keystroke.
const ALL_PRODUCTS = categoriesData.flatMap((cat) =>
  (cat.subcategories || [])
    .filter((item) => item.name)
    .map((item) => ({
      name: item.name,
      image: item.image,
      brand: item.details?.Brand || "",
      category: cat.category,
    }))
);

// Brand chips are derived from the catalogue so they can never point at a term
// that returns nothing — SearchResults matches on details values too.
const TOP_BRANDS = Object.entries(
  ALL_PRODUCTS.reduce((acc, p) => {
    if (p.brand) acc[p.brand] = (acc[p.brand] || 0) + 1;
    return acc;
  }, {})
)
  .sort((a, b) => b[1] - a[1])
  .slice(0, 6)
  .map(([brand]) => brand);

const CATEGORIES = categoriesData.map((cat) => ({
  id: cat.id,
  name: cat.category,
}));

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.04 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

const productPath = (product) =>
  `/category/${slugify(product.category)}/${slugify(product.name)}`;

// Splits a label around the typed term so the match can be emphasised.
const highlight = (label, term) => {
  const idx = label.toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) return label;
  return (
    <>
      {label.slice(0, idx)}
      <mark className="hero-mark">{label.slice(idx, idx + term.length)}</mark>
      {label.slice(idx + term.length)}
    </>
  );
};

/**
 * Search banner. Deliberately just a headline and a search box — the
 * merchandising rows below (deals, categories, best sellers) do the product
 * discovery, so anything more here only duplicates them and pushes the priced
 * offers off the first screen.
 */
const HeroSection = () => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  // Both the category menu and the suggestions panel are portaled to <body>
  // (see below) so they can escape the hero section's `overflow: hidden` —
  // that clip exists to contain the decorative background graphics, and was
  // silently cutting both panels off the instant they grew past the section's
  // bottom edge (the suggestions panel especially: 5 of its 6 rows were
  // invisible, clipped, for any query with more than one match). Being
  // portaled, each panel's position has to be computed from its trigger's
  // real screen coordinates rather than expressed in CSS.
  const [menuRect, setMenuRect] = useState(null);
  const [suggestionsRect, setSuggestionsRect] = useState(null);
  const searchRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const suggestionsRef = useRef(null);
  const navigate = useNavigate();

  // Close both portaled panels on an outside click — "outside" means outside
  // the search bar AND both portals, since neither lives in the search bar's
  // own DOM subtree any more.
  useEffect(() => {
    const onPointerDown = (e) => {
      const inSearch = searchRef.current && searchRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      const inSuggestions =
        suggestionsRef.current && suggestionsRef.current.contains(e.target);
      if (!inSearch && !inMenu && !inSuggestions) {
        setSuggestions([]);
        setActiveIndex(-1);
        setIsCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  // A fixed-position portal has to track its trigger: close on any scroll or
  // resize rather than re-measuring continuously, which is the simple,
  // standard way to keep a portaled panel from drifting off its trigger.
  // `true` catches scroll on an inner ancestor, not just the window — but that
  // also catches the panel's *own* overflow scrolling, which closed the panel
  // the moment the user tried to scroll its list. Scroll events originating
  // inside either panel are therefore ignored; only the page moving under
  // them closes them.
  useEffect(() => {
    if (!isCategoryOpen && suggestions.length === 0) return undefined;
    const close = (e) => {
      const target = e?.target;
      const insidePanel =
        target instanceof Node &&
        ((menuRef.current && menuRef.current.contains(target)) ||
          (suggestionsRef.current && suggestionsRef.current.contains(target)));
      if (insidePanel) return;
      setIsCategoryOpen(false);
      setSuggestions([]);
      setActiveIndex(-1);
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [isCategoryOpen, suggestions.length]);

  // Height available between a trigger's bottom edge and the viewport's, so a
  // portaled panel is never taller than the room it actually has. Written as
  // an inline style it overrides the stylesheet, so it clamps to the same
  // 20rem cap the CSS uses; the floor keeps a couple of rows scrollable even
  // on a very short viewport.
  const availableHeight = (rect) =>
    Math.min(320, Math.max(window.innerHeight - rect.bottom - 24, 160));

  // The two panels are siblings in the same search bar, so "click outside"
  // alone doesn't separate them — clicking the search field to open
  // suggestions is not "outside" the category menu's own search-bar wrapper,
  // so it never closed on its own. Each opener explicitly closes the other.
  const openCategoryMenu = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMenuRect({
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width,
      maxHeight: availableHeight(rect),
    });
    setIsCategoryOpen(true);
    setSuggestions([]);
    setActiveIndex(-1);
  };

  // Shared by both places suggestions get shown (typing and refocusing the
  // field) so the panel's position is always measured fresh, not just its list.
  const showSuggestions = (list) => {
    setSuggestions(list);
    if (list.length === 0) return;
    setIsCategoryOpen(false);
    const rect = searchRef.current?.getBoundingClientRect();
    if (rect) {
      setSuggestionsRect({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width,
        maxHeight: availableHeight(rect),
      });
    }
  };

  const runQuery = (value) => {
    const term = value.trim().toLowerCase();
    if (!term) return [];

    // Ranked so exact-prefix product names beat brand and category matches.
    const matches = [];
    for (const product of ALL_PRODUCTS) {
      const name = product.name.toLowerCase();
      let rank = -1;
      if (name.startsWith(term)) rank = 0;
      else if (name.includes(term)) rank = 1;
      else if (product.brand.toLowerCase().includes(term)) rank = 2;
      else if (product.category.toLowerCase().includes(term)) rank = 3;
      if (rank !== -1) matches.push({ ...product, rank });
    }
    return matches.sort((a, b) => a.rank - b.rank).slice(0, SUGGESTION_LIMIT);
  };

  const handleQueryChange = (value) => {
    setQuery(value);
    setActiveIndex(-1);
    showSuggestions(runQuery(value));
  };

  const closeSuggestions = () => {
    setSuggestions([]);
    setActiveIndex(-1);
  };

  const goToProduct = (product) => {
    closeSuggestions();
    navigate(productPath(product));
  };

  const handleSearch = (term = query) => {
    const value = term.trim();
    if (!value) return;
    closeSuggestions();
    navigate(`/search?q=${encodeURIComponent(value)}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      closeSuggestions();
      return;
    }
    if (e.key === "Enter") {
      if (activeIndex >= 0 && suggestions[activeIndex]) {
        e.preventDefault();
        goToProduct(suggestions[activeIndex]);
      } else {
        handleSearch();
      }
      return;
    }
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    }
  };

  // The category grid is the target, not the merchandising block as a whole —
  // that starts with the deals strip.
  const scrollToCategories = () => {
    const section =
      document.getElementById("shop-by-category") ||
      document.getElementById("categories-section");
    if (!section) return;
    const y = section.getBoundingClientRect().top + window.pageYOffset - 70;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const selectCategory = (selected) => {
    setCategory(selected);
    setIsCategoryOpen(false);

    if (selected === "all") {
      scrollToCategories();
      setTimeout(() => setCategory(""), 800);
    } else if (selected) {
      navigate(`/category/${slugify(selected)}`);
    }
  };

  const handleCategoryKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      setIsCategoryOpen(false);
    }
  };

  return (
    <section className="hero-section">
      <div className="hero-accent" aria-hidden="true" />
      <NetworkOverlay />

      <div className="hero-inner">
        <motion.div
          className="hero-copy"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.span variants={itemVariants} className="hero-badge">
            <Sparkles size={14} />
            Enterprise IT Marketplace
          </motion.span>

          {/* The h1 states what the page is, in the same words as the <title>
              and the body copy. It used to be the slogan now sitting in the
              subtitle, which shared no vocabulary with either — so the page
              read, to a crawler, as though its heading described a different
              page. Keep these three in step when editing. */}
          <motion.h1 variants={itemVariants} className="hero-title">
            Buy{" "}
            <span className="hero-title-accent">enterprise IT hardware</span>{" "}
            online
          </motion.h1>

          <motion.p variants={itemVariants} className="hero-subtitle">
            Find the right servers, storage, networking, security and software
            in seconds, from the brands you already trust.
          </motion.p>

          {/* Search */}
          <motion.div
            variants={itemVariants}
            ref={searchRef}
            className="hero-search-wrap"
          >
            <div className="hero-search">
              <div className="hero-category" onKeyDown={handleCategoryKeyDown}>
                <button
                  type="button"
                  ref={triggerRef}
                  className="hero-category-trigger"
                  onClick={() =>
                    isCategoryOpen ? setIsCategoryOpen(false) : openCategoryMenu()
                  }
                  aria-haspopup="listbox"
                  aria-expanded={isCategoryOpen}
                >
                  <span className="hero-category-label">
                    {category && category !== "all" ? category : "All Categories"}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`hero-category-chevron ${
                      isCategoryOpen ? "is-open" : ""
                    }`}
                  />
                </button>

                {isCategoryOpen &&
                  menuRect &&
                  createPortal(
                    <ul
                      ref={menuRef}
                      className="hero-category-menu"
                      role="listbox"
                      style={{
                        top: menuRect.top,
                        left: menuRect.left,
                        minWidth: Math.max(menuRect.width, 240),
                        maxHeight: menuRect.maxHeight,
                      }}
                    >
                      <li>
                        <button
                          type="button"
                          role="option"
                          aria-selected={category === "all"}
                          className="hero-category-option"
                          onClick={() => selectCategory("all")}
                        >
                          Browse all categories
                        </button>
                      </li>
                      {CATEGORIES.map((cat) => (
                        <li key={cat.id}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={category === cat.name}
                            className={`hero-category-option ${
                              category === cat.name ? "is-active" : ""
                            }`}
                            onClick={() => selectCategory(cat.name)}
                          >
                            {cat.name}
                          </button>
                        </li>
                      ))}
                    </ul>,
                    document.body
                  )}
              </div>

              <div className="hero-search-field">
                <Search className="hero-search-icon" size={18} />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => handleQueryChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={() => showSuggestions(runQuery(query))}
                  placeholder="Search products, brands or categories…"
                  aria-label="Search products"
                  autoComplete="off"
                  className="hero-search-input"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => {
                      setQuery("");
                      closeSuggestions();
                    }}
                    aria-label="Clear search"
                    className="hero-clear"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={() => handleSearch()}
                className="hero-search-btn"
              >
                <Search size={18} />
                <span>Search</span>
              </button>
            </div>

            {/* Suggestions */}
            {suggestions.length > 0 &&
              suggestionsRect &&
              createPortal(
                <div
                  ref={suggestionsRef}
                  className="hero-suggestions"
                  style={{
                    top: suggestionsRect.top,
                    left: suggestionsRect.left,
                    width: suggestionsRect.width,
                    maxHeight: suggestionsRect.maxHeight,
                  }}
                >
                {suggestions.map((item, index) => (
                  <button
                    type="button"
                    key={`${item.category}-${item.name}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => goToProduct(item)}
                    className={`hero-suggestion ${
                      index === activeIndex ? "is-active" : ""
                    }`}
                  >
                    <span className="hero-suggestion-thumb">
                      <img
                        src={productImageUrl(item.image)}
                        srcSet={productImageSrcSet(item.image)}
                        // Search-suggestion thumbnail — a fixed ~40px square,
                        // so the 160w variant is ample even at 2x.
                        sizes="40px"
                        alt=""
                        loading="lazy"
                        decoding="async"
                        onError={applyImageFallback}
                      />
                    </span>
                    <span className="hero-suggestion-text">
                      <span className="hero-suggestion-name">
                        {highlight(item.name, query)}
                      </span>
                      <span className="hero-suggestion-meta">
                        {item.category}
                        {item.brand ? ` · ${item.brand}` : ""}
                      </span>
                    </span>
                    <ChevronRight size={16} className="hero-suggestion-cue" />
                  </button>
                ))}

                <button
                  type="button"
                  onClick={() => handleSearch()}
                  className="hero-suggestions-footer"
                >
                  See all results for “{query.trim()}”
                  <ArrowRight size={14} />
                </button>
                </div>,
                document.body
              )}
          </motion.div>

          {/* Popular brands */}
          {TOP_BRANDS.length > 0 && (
            <motion.div variants={itemVariants} className="hero-chips">
              <span className="hero-chips-label">
                <TrendingUp size={14} />
                Popular
              </span>
              {TOP_BRANDS.map((brand) => (
                <button
                  key={brand}
                  type="button"
                  onClick={() => handleSearch(brand)}
                  className="hero-chip"
                >
                  {brand}
                </button>
              ))}
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
